import { Autocomplete, TextField, Chip } from '@mui/material';
import { Check } from '@mui/icons-material';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import type { FieldValues, Path, RegisterOptions, UseFormRegister } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

interface Option {
  label: string;
  value: string;
}

interface MultiSelectInputProps<T extends FieldValues> {
  label?: string;
  options: Option[];
  className?: string;
  error?: FieldValues['errors'];
  isDisabled?: boolean;
  placeholder?: string;
  isSecondary?: boolean;
  onChange?: (selected: Option[]) => void;
  value?: Option[];
  defaultValue?: Option[];
  register?: UseFormRegister<T>;
  registerName?: Path<T>;
  registerProps?: RegisterOptions<T, Path<T>>;
  limitTags?: number;
  setValue?: <K extends keyof FieldValues>(
    name: K,
    value: FieldValues[K],
    options?: Partial<{
      shouldValidate: boolean;
      shouldDirty: boolean;
      shouldTouch: boolean;
    }>
  ) => void;
}

export const MultiSelectInput = <T extends FieldValues>({
  label,
  options,
  className,
  error,
  isDisabled,
  placeholder,
  isSecondary,
  onChange,
  value,
  defaultValue,
  register,
  registerName,
  registerProps,
  setValue,
  limitTags = 2,
}: MultiSelectInputProps<T>) => {
  const { t } = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<Option[]>(defaultValue ?? value ?? []);

  useEffect(() => {
    if (!value) return;
    setSelectedOptions(value);
  }, [value]);

  useEffect(() => {
    if (!setValue || !register || !registerName) return;
    setValue(
      registerName,
      selectedOptions.map((option) => option.value)
    );
  }, [selectedOptions, setValue, register, registerName]);

  const handleChange = (_event: React.SyntheticEvent, newValue: Option[]) => {
    setSelectedOptions(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div className={twMerge(clsx('relative max-w-xl', className))}>
      <Autocomplete
        multiple
        limitTags={limitTags}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        disabled={isDisabled}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, optValue) => option.value === optValue.value}
        sx={{
          '& .MuiAutocomplete-tag': {
            margin: `4px 4px`,
          },
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'wrap',
            gap: `4px 4px`,
            padding: `4px 4px`,
          },
          '.MuiInputBase-root': {
            padding: '8px 0px 8px 8px',
          },
        }}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={props.id}
            className={clsx('flex items-center gap-4 px-8 py-2 hover:bg-blue-500 hover:text-white cursor-pointer', {
              'bg-blue-500 text-white': selected,
            })}>
            <span className="relative w-5 h-5 border border-gray-300 rounded-[6px]">
              <Check
                className={clsx(
                  'text-primary absolute w-3 h-3 transition-opacity duration-300 ease-in-out z-10 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2',
                  {
                    'opacity-100': selected && !isDisabled,
                    'opacity-50': selected && isDisabled,
                    'opacity-0': !selected,
                  }
                )}
              />
            </span>
            <span
              className={clsx('text-ellipsis overflow-hidden max-w-lg', {
                'text-primary': !selected,
                'text-white': selected,
              })}>
              {option.label}
            </span>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              label={option.label}
              key={option.value}
              className="!text-ellipsis !overflow-hidden max-w-44 bg-secondary-light rounded-lg w-max px-2"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder ?? t('common.select')}
            error={!!error}
            helperText={error?.message}
            className={clsx('w-full rounded-md relative border-secondary-dark', {
              'border-1 bg-transparent text-primary': isSecondary,
              'bg-secondary-dark text-primary-dark': !isSecondary,
              'bg-gray-200': isDisabled,
              'border-red-500': error,
            })}
          />
        )}
      />
      {register && registerName && (
        <input {...register(registerName, registerProps)} hidden value={selectedOptions.map((option) => option.value).join(',')} />
      )}
    </div>
  );
};
